import * as React from 'react';
import {useState, useTransition} from 'react';
import {graphql, useLazyLoadQuery, useMutation} from 'react-relay';
import {AlertTriangle, Minus, Plus} from 'lucide-react';
import {OpsProductsPageQuery} from './__generated__/OpsProductsPageQuery.graphql';
import {OpsProductsPageAdjustMutation} from './__generated__/OpsProductsPageAdjustMutation.graphql';
import {OpsProductsPageUpdateMutation} from './__generated__/OpsProductsPageUpdateMutation.graphql';
import {OpsProductsPageCreateMutation} from './__generated__/OpsProductsPageCreateMutation.graphql';
import {ghostButton, inputClass, primaryButton} from './lib';

const QUERY = graphql`
    query OpsProductsPageQuery {
        products {
            id
            slug
            kind
            name
            make
            model
            category
            rentable
            price
            unit
            packSize
            quantityOnHand
            reorderLevel
            lowStock
            properties
            forProduct { id }
        }
    }`;

const ADJUST = graphql`
    mutation OpsProductsPageAdjustMutation($input: AdjustProductStockInput!) {
        adjustProductStock(input: $input) {
            product { id quantityOnHand lowStock }
            userErrors { message }
        }
    }`;

const UPDATE = graphql`
    mutation OpsProductsPageUpdateMutation($input: UpdateProductInput!) {
        updateProduct(input: $input) {
            product { id reorderLevel lowStock }
            userErrors { message }
        }
    }`;

const CREATE = graphql`
    mutation OpsProductsPageCreateMutation($input: CreateProductInput!) {
        createProduct(input: $input) {
            product { id }
            userErrors { message }
        }
    }`;

type Product = OpsProductsPageQuery['response']['products'][number];

const Stock: React.FC<{ product: Product }> = ({product}) => {
    const [adjust, adjusting] = useMutation<OpsProductsPageAdjustMutation>(ADJUST);
    const [update] = useMutation<OpsProductsPageUpdateMutation>(UPDATE);
    const [reorder, setReorder] = useState(String(product.reorderLevel));
    const bump = (delta: number) => adjust({variables: {input: {productId: product.id, delta}}});
    const saveReorder = () => {
        const n = Number(reorder);
        if (Number.isInteger(n) && n >= 0 && n !== product.reorderLevel) update({variables: {input: {productId: product.id, reorderLevel: n}}});
        else setReorder(String(product.reorderLevel));
    };
    return (
        <div className="flex items-center gap-3">
            <div className="flex items-center border border-gray-800">
                <button type="button" onClick={() => bump(-1)} disabled={adjusting || product.quantityOnHand === 0} className="px-2 py-1 text-gray-400 hover:text-white disabled:opacity-30" title="Used one"><Minus className="w-3 h-3"/></button>
                <span className={`w-10 text-center text-sm font-mono ${product.lowStock ? 'text-red-400' : 'text-white'}`}>{product.quantityOnHand}</span>
                <button type="button" onClick={() => bump(1)} disabled={adjusting} className="px-2 py-1 text-gray-400 hover:text-white" title="Received one"><Plus className="w-3 h-3"/></button>
            </div>
            <label className="text-[11px] text-gray-500 flex items-center gap-1">
                reorder at
                <input value={reorder} onChange={(e) => setReorder(e.target.value)} onBlur={saveReorder} onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                       className="w-10 bg-black border border-gray-800 px-1 py-0.5 text-center text-gray-300 text-xs"/>
            </label>
        </div>
    );
};

const Price: React.FC<{ product: Product }> = ({product}) => {
    const conf = (product.properties as Record<string, unknown>)?.confidence;
    return product.price
        ? <span className="text-xs text-gray-400" title={conf ? `${conf} confidence` : undefined}>${Number(product.price).toLocaleString(undefined, {maximumFractionDigits: 2})}{conf && conf !== 'high' && <span className="text-gray-600">*</span>}</span>
        : <span className="text-xs text-gray-600">no price</span>;
};

const NewConsumable: React.FC<{ machines: Product[]; onDone: () => void }> = ({machines, onDone}) => {
    const [commit, inFlight] = useMutation<OpsProductsPageCreateMutation>(CREATE);
    const [form, setForm] = useState({make: '', model: '', forProductId: machines[0]?.id ?? '', unit: 'spool', packSize: '', price: '', quantityOnHand: '0', reorderLevel: '1'});
    const [error, setError] = useState<string | null>(null);
    const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm({...form, [k]: e.target.value});
    const submit = () => {
        setError(null);
        commit({
            variables: {input: {
                kind: 'CONSUMABLE', category: 'CONSUMABLE', make: form.make.trim(), model: form.model.trim(),
                forProductId: form.forProductId || null, unit: form.unit || null, packSize: form.packSize.trim() || null,
                price: form.price ? Number(form.price) : null, quantityOnHand: Number(form.quantityOnHand) || 0, reorderLevel: Number(form.reorderLevel) || 0,
            }},
            onCompleted: (res) => {
                if (res.createProduct?.product) onDone();
                else setError(res.createProduct?.userErrors.map((u) => u.message).join('; ') ?? 'Something went wrong');
            },
            onError: (e) => setError(e.message),
        });
    };
    return (
        <div className="border border-gray-800 p-4 mb-6 grid md:grid-cols-4 gap-3 items-end">
            <label className="text-[11px] text-gray-500">Make<input value={form.make} onChange={set('make')} className={inputClass} placeholder="Formlabs"/></label>
            <label className="text-[11px] text-gray-500">Material<input value={form.model} onChange={set('model')} className={inputClass} placeholder="Grey Resin V5"/></label>
            <label className="text-[11px] text-gray-500">For machine
                <select value={form.forProductId} onChange={set('forProductId')} className={inputClass}>
                    <option value="">—</option>
                    {machines.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
            </label>
            <label className="text-[11px] text-gray-500">Unit
                <select value={form.unit} onChange={set('unit')} className={inputClass}><option>spool</option><option>cartridge</option><option>bottle</option><option>box</option></select>
            </label>
            <label className="text-[11px] text-gray-500">Pack size<input value={form.packSize} onChange={set('packSize')} className={inputClass} placeholder="1 L / 0.5 kg"/></label>
            <label className="text-[11px] text-gray-500">Price $<input value={form.price} onChange={set('price')} className={inputClass} inputMode="decimal"/></label>
            <label className="text-[11px] text-gray-500">On hand<input value={form.quantityOnHand} onChange={set('quantityOnHand')} className={inputClass} inputMode="numeric"/></label>
            <label className="text-[11px] text-gray-500">Reorder at<input value={form.reorderLevel} onChange={set('reorderLevel')} className={inputClass} inputMode="numeric"/></label>
            {error && <p className="md:col-span-4 text-red-400 text-xs">{error}</p>}
            <div className="md:col-span-4 flex gap-2">
                <button type="button" onClick={submit} disabled={inFlight || !form.make.trim() || !form.model.trim()} className={primaryButton}>Add consumable</button>
                <button type="button" onClick={onDone} className={ghostButton}>Cancel</button>
            </div>
        </div>
    );
};

// Products: the printers we run and every consumable they use. Stock lives here, not on assets.
const OpsProductsPage: React.FC = () => {
    const [fetchKey, setFetchKey] = useState(0);
    const [, startTransition] = useTransition();
    const data = useLazyLoadQuery<OpsProductsPageQuery>(QUERY, {}, {fetchPolicy: 'network-only', fetchKey});
    const [adding, setAdding] = useState(false);
    const refresh = () => startTransition(() => setFetchKey((k) => k + 1));

    const machines = data.products.filter((p) => p.kind !== 'CONSUMABLE');
    const consumables = data.products.filter((p) => p.kind === 'CONSUMABLE');
    const low = consumables.filter((p) => p.lowStock);
    const byMachine = (id: string | null) => consumables.filter((c) => (c.forProduct?.id ?? null) === id);
    const orphans = byMachine(null);

    return (
        <div>
            <div className="flex items-end justify-between mb-6">
                <h1 className="text-3xl font-display font-bold">Products</h1>
                <button type="button" onClick={() => setAdding(true)} className={`${primaryButton} flex items-center gap-2`}><Plus className="w-4 h-4"/> New consumable</button>
            </div>
            {adding && <NewConsumable machines={machines} onDone={() => { setAdding(false); refresh(); }}/>}

            {low.length > 0 && (
                <p className="mb-6 text-xs text-red-400 flex items-center gap-2"><AlertTriangle className="w-4 h-4"/> {low.length} consumable{low.length === 1 ? '' : 's'} at or below reorder level: {low.map((p) => p.name).join(', ')}</p>
            )}

            <section className="mb-10">
                <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Machines</h2>
                <ul className="border border-gray-800 divide-y divide-gray-900">
                    {machines.map((m) => (
                        <li key={m.id} className="px-4 py-3 flex items-center gap-4">
                            <span className="flex-1">
                                <span className="text-white font-semibold">{m.name}</span>
                                <span className="block text-[11px] text-gray-500">{m.category.replace(/_/g, ' ').toLowerCase()} · {m.rentable ? 'rentable' : 'not for rent'} · {byMachine(m.id).length} consumables</span>
                            </span>
                            <Price product={m}/>
                        </li>
                    ))}
                </ul>
            </section>

            {[...machines.map((m) => ({id: m.id as string | null, title: `${m.name} consumables`, rows: byMachine(m.id)})), {id: null, title: 'Other consumables', rows: orphans}]
                .filter((g) => g.rows.length > 0)
                .map((group) => (
                    <section key={group.id ?? 'none'} className="mb-10">
                        <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">{group.title}</h2>
                        <table className="w-full text-sm">
                            <thead className="text-[11px] uppercase tracking-wider text-gray-500 text-left">
                                <tr><th className="py-1 font-normal">Material</th><th className="py-1 font-normal">Family</th><th className="py-1 font-normal">Pack</th><th className="py-1 font-normal">List</th><th className="py-1 font-normal">Stock</th></tr>
                            </thead>
                            <tbody className="divide-y divide-gray-900 border-y border-gray-800">
                                {group.rows.map((p) => {
                                    const props = (p.properties ?? {}) as Record<string, unknown>;
                                    return (
                                        <tr key={p.id} className={p.lowStock ? 'bg-red-950/20' : ''}>
                                            <td className="py-2 pr-4">
                                                <span className="text-white">{p.model ?? p.name}</span>
                                                {props.legacy ? <span className="ml-2 text-[10px] text-gray-600 uppercase">legacy</span> : null}
                                                {props.note ? <span className="block text-[11px] text-gray-500">{String(props.note)}</span> : null}
                                            </td>
                                            <td className="py-2 pr-4 text-xs text-gray-400">{String(props.family ?? '')}{props.extruder ? <span className="text-gray-600"> · {String(props.extruder)}</span> : null}</td>
                                            <td className="py-2 pr-4 text-xs text-gray-400">{p.packSize} {p.unit}</td>
                                            <td className="py-2 pr-4"><Price product={p}/></td>
                                            <td className="py-2"><Stock product={p}/></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </section>
                ))}
        </div>
    );
};

export default OpsProductsPage;
